import React, { useState } from "react";
import axios from "axios";
import { Sparkles, Loader2, MessageCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { buildWaLink, WA_TEMPLATES } from "@/lib/whatsapp";

const API = process.env.REACT_APP_BACKEND_URL;

const SUGGESTIONS = [
  "My layer flock has soft egg shells and drop in production",
  "Adult dog, 20kg, roundworms and tapeworms suspected",
  "Dairy herd — recurring mastitis after milking",
  "Clinic — need biosecurity for footbaths and treatment rooms",
];

export default function AiProductAsk({ compact = false }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    const description = text.trim();
    if (!description || loading) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/ai/product-recommend`, {
        description,
      });
      setResult(data);
    } catch (err) {
      setError(
        "I couldn't process that just now. Please WhatsApp our technical team."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="rounded-2xl bg-bah-ink text-white p-6 md:p-8 shadow-lg ring-1 ring-white/10"
      data-testid="ai-product-ask"
    >
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-300">
        <Sparkles className="h-4 w-4" />
        AI product assistant
      </div>
      <h3
        className={`mt-3 font-display leading-tight ${
          compact ? "text-xl md:text-2xl" : "text-2xl md:text-3xl"
        }`}
      >
        Describe your case in plain words — we'll suggest Biodeal products.
      </h3>
      <p className="mt-2 text-sm text-white/70 max-w-2xl">
        Grounded in our catalogue only. Not a substitute for veterinary
        supervision — for individual dosing, our team is on WhatsApp.
      </p>

      <form onSubmit={submit} className="mt-5">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Broilers coughing, 3-week-old flock, poor appetite…"
            data-testid="ai-ask-input"
            className="flex-1 rounded-xl bg-white/10 text-white placeholder-white/50 px-4 py-3 text-sm ring-1 ring-white/15 focus:outline-none focus:ring-2 focus:ring-emerald-300"
          />
          <button
            type="submit"
            disabled={loading || !text.trim()}
            data-testid="ai-ask-submit"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-400 text-bah-ink font-bold text-sm px-5 py-3 hover:bg-emerald-300 transition disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Matching…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" /> Ask
              </>
            )}
          </button>
        </div>

        {!result && !loading && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setText(s)}
                data-testid={`ai-ask-suggestion-${i}`}
                className="text-[11px] rounded-full border border-white/15 bg-white/5 px-3 py-1 text-white/80 hover:bg-white/15 hover:text-white transition"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </form>

      {error && (
        <div
          className="mt-4 rounded-lg bg-red-500/10 border border-red-400/30 text-red-100 text-sm px-3 py-2"
          data-testid="ai-ask-error"
        >
          {error}
        </div>
      )}

      {result && (
        <div className="mt-6 rounded-2xl bg-white text-bah-ink p-5" data-testid="ai-ask-result">
          {result.products?.length > 0 ? (
            <>
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-bah-green">
                Suggested from the Biodeal catalogue
              </div>
              <ul className="mt-3 space-y-3">
                {result.products.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-start justify-between gap-3 rounded-xl bg-bah-paper px-4 py-3 border border-bah-line"
                    data-testid={`ai-ask-product-${p.id}`}
                  >
                    <div>
                      <div className="font-display text-lg leading-tight">
                        {p.name}
                      </div>
                      <p className="text-sm text-bah-mute mt-1">{p.why}</p>
                    </div>
                    <Link
                      to={`/products?product=${encodeURIComponent(p.name)}`}
                      className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-bah-green hover:text-bah-orange whitespace-nowrap"
                    >
                      View <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-sm text-bah-ink">
              I couldn't confidently match a Biodeal product to that case.
              Our team will help.
            </p>
          )}
          {result.guidance && (
            <p className="mt-4 text-xs text-bah-mute leading-relaxed">
              {result.guidance}
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href={buildWaLink(
                `${WA_TEMPLATES.technical_general} Context: ${text.trim()}`
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
              data-testid="ai-ask-wa"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp technical team
            </a>
            <button
              type="button"
              onClick={() => {
                setResult(null);
                setText("");
              }}
              data-testid="ai-ask-reset"
              className="btn-ghost-dark"
            >
              Ask something else
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
